import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";

import Panel from "../../components/brand/Panel";
import {
  getServiceChargeSettings,
  updateServiceChargeMechanism,
} from "../../api/financeApi";
import { getApiErrorMessage, isConcurrencyConflict } from "../../utils/concurrency";
import {
  FINANCE_UNITS_TABLE_MAX_HEIGHT,
  unitSortKey,
  useClientTableSort,
} from "./useClientTableSort";

const MECHANISM_HELP = {
  discretionary: "Set each unit’s percentage or amount and an optional reason in the budget Apportionments tab.",
  equal_shares: "Each unit receives an equal share of the Estate Charge (100% ÷ number of units).",
  floor_area: "Each unit's Estate Charge share is based on its square footage relative to the property total.",
};

const formatPct = (value) =>
  value == null ? "—" : `${Number(value).toFixed(4)}%`;

export default function ServiceChargeMechanismPanel({ propertyId, onMessage, onError, onChanged }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mechanism, setMechanism] = useState("");
  const [savedMechanism, setSavedMechanism] = useState("");
  const [estateCode, setEstateCode] = useState("");
  const [savedEstateCode, setSavedEstateCode] = useState("");
  const [options, setOptions] = useState([]);
  const [units, setUnits] = useState([]);
  const [rowVersion, setRowVersion] = useState(1);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      onError?.("");
      const res = await getServiceChargeSettings(propertyId);
      const data = res.data;
      setMechanism(data.service_charge_mechanism || "");
      setSavedMechanism(data.service_charge_mechanism || "");
      setEstateCode(data.estate_code || "");
      setSavedEstateCode(data.estate_code || "");
      setOptions(data.mechanism_options || []);
      setUnits(data.units || []);
      setRowVersion(data.row_version || 1);
    } catch (e) {
      onError?.(getApiErrorMessage(e, "Failed to load service charge settings."));
    } finally {
      setLoading(false);
    }
  }, [propertyId, onError]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    try {
      setSaving(true);
      onError?.("");
      const res = await updateServiceChargeMechanism(propertyId, {
        service_charge_mechanism: mechanism || null,
        estate_code: estateCode,
        row_version: rowVersion,
      });
      setRowVersion(res.data.row_version || rowVersion);
      setSavedMechanism(res.data.service_charge_mechanism || "");
      setEstateCode(res.data.estate_code || "");
      setSavedEstateCode(res.data.estate_code || "");
      setUnits(res.data.units || []);
      onMessage?.("Finance settings saved.");
      onChanged?.();
    } catch (e) {
      if (isConcurrencyConflict(e)) {
        onError?.("Property was updated elsewhere. Reloading settings.");
        await load();
      } else {
        onError?.(getApiErrorMessage(e, "Failed to save service charge mechanism."));
      }
    } finally {
      setSaving(false);
    }
  };

  const estateTotal = units.reduce(
    (sum, unit) => sum + (unit.estate_charge_percentage ?? 0),
    0
  );

  const unitSortAccessors = useMemo(
    () => ({
      unit_code: unitSortKey,
      square_feet: (row) => row.square_feet,
      estate_charge_percentage: (row) => row.estate_charge_percentage,
    }),
    []
  );
  const { sortedRows: sortedUnits, sortField, sortDirection, handleSort } = useClientTableSort(
    units,
    unitSortAccessors,
    "unit_code"
  );

  return (
    <Panel title="Service charge mechanism" noBodyPadding={false} sx={{ mb: 3 }}>
      {loading ? (
        <Skeleton height={160} />
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose how each unit&apos;s share of the Estate Charge is calculated. Every unit in the
            property pays a portion of the Estate Charge. Each Block Charge uses its own mechanism
            among the units assigned to that block.
          </Typography>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-end", mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="service-charge-mechanism-label">Mechanism</InputLabel>
              <Select
                labelId="service-charge-mechanism-label"
                label="Mechanism"
                value={mechanism}
                onChange={(e) => setMechanism(e.target.value)}
              >
                <MenuItem value="">
                  <em>Not set</em>
                </MenuItem>
                {options.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Estate Code"
              value={estateCode}
              onChange={(e) => setEstateCode(e.target.value.toUpperCase())}
              inputProps={{ maxLength: 64 }}
              sx={{ minWidth: 180 }}
            />
            <Button
              variant="contained"
              size="small"
              disabled={
                saving ||
                (mechanism === savedMechanism && estateCode === savedEstateCode)
              }
              onClick={handleSave}
            >
              Save
            </Button>
          </Box>

          {mechanism && MECHANISM_HELP[mechanism] && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {MECHANISM_HELP[mechanism]}
              {mechanism === "floor_area" && " Ensure each unit has square footage recorded."}
            </Alert>
          )}

          {units.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Estate Charge shares
              </Typography>
              <TableContainer sx={{ maxHeight: FINANCE_UNITS_TABLE_MAX_HEIGHT }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sortDirection={sortField === "unit_code" ? sortDirection : false}>
                        <TableSortLabel
                          active={sortField === "unit_code"}
                          direction={sortField === "unit_code" ? sortDirection : "asc"}
                          onClick={() => handleSort("unit_code")}
                        >
                          Unit
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sortDirection={sortField === "square_feet" ? sortDirection : false}>
                        <TableSortLabel
                          active={sortField === "square_feet"}
                          direction={sortField === "square_feet" ? sortDirection : "asc"}
                          onClick={() => handleSort("square_feet")}
                        >
                          Sq ft
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        align="right"
                        sortDirection={sortField === "estate_charge_percentage" ? sortDirection : false}
                      >
                        <TableSortLabel
                          active={sortField === "estate_charge_percentage"}
                          direction={
                            sortField === "estate_charge_percentage" ? sortDirection : "asc"
                          }
                          onClick={() => handleSort("estate_charge_percentage")}
                        >
                          Estate Charge %
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedUnits.map((unit) => (
                      <TableRow key={unit.unit_id}>
                        <TableCell>{unit.unit_name}</TableCell>
                        <TableCell align="right">{unit.square_feet ?? "—"}</TableCell>
                        <TableCell align="right">
                          {formatPct(unit.estate_charge_percentage)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={2} sx={{ fontWeight: 600 }}>
                        Total
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {formatPct(estateTotal)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </>
      )}
    </Panel>
  );
}
